// Paths reachable without a Clerk session. The inspector page and API carry
// their own signed-link check instead.
export const PUBLIC_ROUTE_PATTERNS = [
  "/login(.*)",
  "/sign-in(.*)",
  "/inspect/(.*)",
  "/api/inspect/(.*)",
];

// Anchored and case-insensitive, as Clerk's createRouteMatcher compiles these patterns.
const COMPILED = PUBLIC_ROUTE_PATTERNS.map((p) => {
  const body = p
    .split("(.*)")
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("(.*)");
  return new RegExp(`^${body}$`, "i");
});

export function isPublicPath(pathname: string): boolean {
  return COMPILED.some((re) => re.test(pathname));
}

// Inspector links carry their token in the path. These keep it out of Sentry.
// Every `/inspect/<token>` with what follows up to whitespace or a quote; the query and
// fragment after an inspector path are dropped.
const INSPECT_URL = /\/inspect\/[^/?#\s"'<>]*([^?#\s"'<>]*)(?:[?#][^\s"'<>]*)?/gi;

/** `/inspect/<token>/…` and `/api/inspect/<token>/…` with every token replaced and no query. */
export function redactInspectUrl(url: string): string;
export function redactInspectUrl<T>(url: T): T;
export function redactInspectUrl(url: unknown): unknown {
  return typeof url === "string" ? url.replace(INSPECT_URL, "/inspect/[token]$1") : url;
}

type Crumb = { data?: Record<string, unknown> };
type SentryLike = {
  message?: string;
  request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
  transaction?: string;
  breadcrumbs?: Crumb[];
  spans?: Array<{ description?: string; data?: Record<string, unknown> }>;
  exception?: { values?: Array<{ value?: string }> };
  tags?: Record<string, unknown>;
  contexts?: { trace?: { data?: Record<string, unknown> } };
};

function redactData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const [k, v] of Object.entries(data)) data[k] = redactInspectUrl(v);
}

/** Sentry `beforeBreadcrumb`: navigation/fetch/xhr crumbs keep their URLs in `data`. */
export function redactBreadcrumb<T extends Crumb>(crumb: T): T {
  redactData(crumb.data);
  return crumb;
}

/** Sentry `beforeSend` / `beforeSendTransaction`. */
export function redactSentryEvent<T extends SentryLike>(event: T): T {
  const req = event.request;
  if (req?.url && req.url !== redactInspectUrl(req.url)) {
    req.url = redactInspectUrl(req.url);
    delete req.query_string;
  }
  if (req?.headers) redactData(req.headers);
  event.message = redactInspectUrl(event.message);
  event.transaction = redactInspectUrl(event.transaction);
  for (const ex of event.exception?.values ?? []) ex.value = redactInspectUrl(ex.value);
  redactData(event.tags);
  redactData(event.contexts?.trace?.data);
  event.breadcrumbs?.forEach(redactBreadcrumb);
  for (const span of event.spans ?? []) {
    span.description = redactInspectUrl(span.description);
    redactData(span.data);
  }
  return event;
}
